"use client";
import { Card } from "@cmp-src/data-display/Card/Card";
import {
  MobileTopMargin,
  SurfaceCardClasses
} from "@cmp-src/data-display/Card/classes";
import { VirtualizedList } from "@cmp-src/data-display/VirtualizedList/VirtualizedList";
import { StartEndTimePicker } from "@cmp-src/date-time/StartEndTimePicker/StartEndTimePicker";
import { FloorSelector } from "@cmp-src/divisions/FloorSelector/FloorSelector";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import { SelectOffices } from "@cmp-src/form/OfficeSelectorInput/SelectOffices";
import { TextInput } from "@cmp-src/form/TextInput";
import { SelectItem, UnifiedSelect } from "@cmp-src/form/UnifiedSelect";
import {
  ArrowDownIcon,
  CloseIcon,
  FilterIcon,
  SearchIcon
} from "@cmp-src/icons/icons";
import { UserLabel } from "@cmp-src/labels/UserLabel/UserLabel";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { Button } from "@cmp-src/primitives/button";
import { Switch } from "@cmp-src/primitives/Switch";
import { AnimatedTabs } from "@cmp-src/tabs/AnimatedTabs/AnimatedTabts";
import {
  IResource,
  ISchedule,
  IUserFitSimple,
  IUserFull,
  MapTab
} from "@gettactic/api";
import {
  cn,
  getDefaultsHoursFromOffice,
  UseDisclosureReturn,
  useDisclosure
} from "@gettactic/helpers";
import { getNowDate } from "@gettactic/helpers/src/date-time/getNowDate";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { DEFAULT_MAP_TABS } from "@gettactic/helpers/src/map/mapTabsSettings";
import { isResourceAvailable } from "@gettactic/helpers/src/resources/isResourceAvailable";
import {
  useAuthenticated,
  useSettings,
  useUserListPaginated
} from "@gettactic/hooks";
import { MenuIcon } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MapFiltersCardSearchNoResults } from "@/features/map/components/MapFiltersCard/MapFiltersCardSearchNoResults";
import { useMap } from "@/features/map/MapProvider";
import { useReserve } from "@/features/reserve/providers/context";
import { useRouter } from "@/lib/useRouter";
import { EmbeddedScheduleButton } from "./EmbeddedScheduleButton";
import { getPlaceholderText, getResourcesVirtualizedListTab } from "./helpers";

const AmenitiesFilterTrigger = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { count?: number }
>(({ count = 0, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "relative flex !w-fit cursor-pointer items-center justify-end text-secondary-text outline-none focus:outline-none focus-visible:outline-none active:text-primary",
        className
      )}
      {...props}
    >
      {!!count && (
        <div className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-xs">
          {count}
        </div>
      )}
      <FilterIcon className="size-4" />
    </div>
  );
});

const AmenitiesFilterButtonTrigger = React.forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & { count?: number }
>(({ count = 0, className, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      size="sm"
      variant="outline"
      className={cn("relative h-9 px-3", className)}
      {...props}
    >
      {!!count && (
        <div className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-xs">
          {count}
        </div>
      )}
      <FilterIcon className="size-4" />
    </Button>
  );
});

const classes = {
  FloorAndTimeWrapper: "flex gap-2 mt-2",
  TabsContent: "relative data-[state=active]:flex flex-1",
  VirtualizedList: "absolute inset-0",
  ListItem: "hover:cursor-pointer select-none h-[86px]",
  TabsContainer: "h-full flex flex-col flex-1",
  TabsList: "flex items-center justify-between",
  TabContentScrollArea: "!absolute inset-0",
  SearchWrapper:
    "mb-4 active:-outline-offset-2 py-2 transition-all duration-300 ease-in-out data-[state=closed]:max-h-0 data-[state=closed]:opacity-0 data-[state=closed]:py-0 data-[state=open]:max-h-20 data-[state=open]:opacity-100",
  SearchIcon: "size-4 text-neutral-600",
  SearchCloseIcon: "size-4 text-neutral-600 hover:text-neutral-1000"
};

export const MapFiltersCardClasses = classes;

export interface MapFiltersCardProps {
  dayViewDisclosure?: UseDisclosureReturn;
  isCompact?: boolean;
  isModal?: boolean;
}

export const MapFiltersCard: React.FC<MapFiltersCardProps> = ({
  dayViewDisclosure,
  isCompact,
  isModal
}) => {
  const { t } = useTranslation("common");
  const searchRef = React.useRef<HTMLInputElement>(null);
  const router = useRouter();
  // const [searchText, setSearchText] = React.useState("");

  const {
    currentOffice,
    changeOffice,
    viewingDivision,
    setViewingDivision,
    floors,
    floor,
    setFloor,
    changeStartDate,
    changeEndTime,
    changeTimeRange,
    startDate,
    endDate,
    timeMode,
    setTimeMode,
    resources: allResources,
    schedulable,
    events,
    amenities,
    applyAmenitiesFilter,
    amenitiesFilter,
    isMapEditing,
    userFocusDisclosure,
    mapFiltersDisclosure,
    fullscreenMapDisclosure,
    setLocateResourceId,
    filterQuery: searchText,
    setFilterQuery: setSearchText,
    defaultTab: selectedTab,
    setSection: setSelectedTab
  } = useMap();

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const { isReserveOpen } = useReserve();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const { setUserContext } = useAuthenticated();
  const { isOfficePrivacyEnabled, mapTabs } = useSettings();
  const isOrgPrivacyEnabled = isOfficePrivacyEnabled(currentOffice?.id);

  // The organization picks the order and which tabs exist; privacy mode still
  // wins over the setting, since the People tab leaks who is in the office.
  const visibleTabs = React.useMemo(() => {
    if (!isOrgPrivacyEnabled) return mapTabs;
    const withoutPeople = mapTabs.filter((tab) => tab !== "people");
    // An organization that made People its only visible tab would be left with
    // an empty side panel once privacy strips it — fall back to the product
    // default rather than render no tabs at all.
    return withoutPeople.length > 0
      ? withoutPeople
      : DEFAULT_MAP_TABS.filter((tab) => tab !== "people");
  }, [mapTabs, isOrgPrivacyEnabled]);
  const firstTab = visibleTabs[0] ?? "desks";
  const activeTab = visibleTabs.includes(selectedTab as MapTab)
    ? (selectedTab as MapTab)
    : firstTab;

  const setSelectedDate = (date: Date | Date[] | undefined) => {
    const dates = Array.isArray(date) ? date : [date];
    if (dates.length === 1 && dates[0]) {
      // Preserve the current time when changing the date
      const newDate = dates[0];
      if (startDate) {
        newDate.setHours(startDate.getHours());
        newDate.setMinutes(startDate.getMinutes());
        newDate.setSeconds(startDate.getSeconds());
        newDate.setMilliseconds(startDate.getMilliseconds());
      }
      changeStartDate(newDate);
      setTimeMode("manual");
    } else {
      // TODO: Handle multiple dates
      console.warn("Multiple dates selected:", dates);
      changeStartDate(dates[0] || getNowDate());
    }
  };

  const onFloorChange = (floorId?: string) => {
    // Don't clear the floor if an empty string is passed - this happens when FloorSelector initializes
    if (floorId === "" || floorId === undefined) {
      return;
    }

    const foundFloor = floors?.find((floor) => floor.id === floorId);
    if (foundFloor) {
      setFloor(foundFloor);
    }
  };

  // const [selectedTab, setSelectedTab] = React.useState<
  //   "people" | "rooms" | "desks" | "other"
  // >(defaultTab || (isOrgPrivacyEnabled ? "rooms" : "people"));
  const [showHighlighter, setShowHighlighter] = React.useState(true);
  const searchDisclosure = useDisclosure();

  // API should always receive the query (from local input / URL)
  const { allPages: allUsers, isFetching } = useUserListPaginated(
    searchText,
    300
  );

  const handleTabChange = (value: string) => {
    // If search icon tab is clicked, open search instead
    if (value === "search-tab") {
      handleSearchOpenChange(true);
      return;
    }

    // Temporarily hide highlighter during tab animation
    setShowHighlighter(false);
    setSelectedTab(value as "people" | "rooms" | "desks" | "other");

    // Re-enable highlighter after animation completes
    setTimeout(() => {
      setShowHighlighter(true);
    }, 350); // Wait for spring animation to complete
  };

  const users = React.useMemo(() => {
    // Occupied users come from schedule slots (full user objects);
    // dedicated-desk owners come from resource assignments (slim user
    // objects) — the discriminant is `dedicatedResource`.
    type PersonEntry =
      | { user: IUserFull; schedules: ISchedule[]; dedicatedResource?: never }
      | {
          user: IUserFitSimple;
          schedules: ISchedule[];
          dedicatedResource: IResource;
        };
    const scheduleUsers: Record<string, PersonEntry> = {};
    for (const schedulableItem of schedulable || []) {
      const schedules = schedulableItem.schedules || [];
      for (const schedule of schedules) {
        if (schedule.slots) {
          for (const slot of schedule.slots) {
            // A walk-up check-in with no real person attached is stamped
            // with a "walkup" placeholder identity — it isn't a real person
            // in the office, so it must not show up in the roster.
            if (slot.user?.roles?.includes("walkup")) continue;
            // Privacy-protected occupants come back from /occupied with a
            // "Private" placeholder id — not a real person, so skip it.
            if (slot.user?.id === "Private") continue;
            if (slot.user && !scheduleUsers[slot.user.id]) {
              scheduleUsers[slot.user.id] = { user: slot.user, schedules: [] };
            }
            scheduleUsers[slot.user.id].schedules = [
              ...(scheduleUsers[slot.user.id].schedules || []),
              {
                ...schedule,
                resource: schedulableItem,
                start: schedule.start,
                end: schedule.end,
                slots: schedule.slots
              }
            ];
          }
        }
      }
    }

    // Dedicated desks put their owner in the office even without a
    // booking — add them from the resources list (occupied users win).
    for (const resource of allResources || []) {
      if (!resource.is_dedicated) continue;
      for (const owner of resource.user_visible_assignments || []) {
        if (scheduleUsers[owner.id]) continue;
        scheduleUsers[owner.id] = {
          user: owner,
          schedules: [],
          dedicatedResource: resource
        };
      }
    }

    const allEntries = Object.values(scheduleUsers);

    if (searchText) {
      const query = searchText.toLowerCase();
      return allEntries?.filter((entry) => {
        const email = "email" in entry.user ? entry.user.email : undefined;
        const title = "title" in entry.user ? entry.user.title : undefined;
        return (
          entry.user.name?.toLowerCase().includes(query) ||
          email?.toLowerCase().includes(query) ||
          title?.toLowerCase().includes(query)
        );
      });
    }

    return allEntries;
  }, [schedulable, allResources, searchText]);

  const resources = React.useMemo(() => {
    let availableResources =
      (searchText
        ? allResources?.filter((resource) => {
            return (
              resource.name.toLowerCase().includes(searchText.toLowerCase()) ||
              resource.tags?.some((tag) =>
                tag.name.toLowerCase().includes(searchText.toLowerCase())
              )
            );
          })
        : allResources) || [];

    availableResources = availableResources
      .filter((resource) => {
        const schedulableResource = schedulable?.find(
          (schedulableResource) => schedulableResource.id === resource.id
        );
        return isResourceAvailable(
          authenticatedUser,
          { ...resource, ...schedulableResource },
          {
            authenticatedUser,
            officeTimeZone: currentOffice?.time_zone ?? "UTC",
            office: currentOffice,
            resourceType: resource.type,
            allowReserveOnBehalf: false,
            meetingsDisabled: false,
            startDate: new Date()
          }
        );
      })
      .sort((a, b) => {
        const aHasFav = a.favorite_id != null;
        const bHasFav = b.favorite_id != null;
        return aHasFav === bHasFav ? 0 : aHasFav ? -1 : 1;
      });

    return {
      desks:
        availableResources?.filter((resource) => resource.type === "desk") ||
        [],
      rooms:
        availableResources?.filter(
          (resource) => resource.type === "meeting_room"
        ) || [],
      other:
        availableResources?.filter(
          (resource) =>
            resource.type !== "desk" && resource.type !== "meeting_room"
        ) || []
    };
  }, [allResources, searchText, schedulable]);

  const handleSearchOpenChange = (isOpen?: boolean) => {
    if (isOpen) {
      searchDisclosure.open();
      setShowHighlighter(true);
      setTimeout(() => {
        searchRef.current?.focus();
      }, 10);
      return;
    }
    searchDisclosure.close();
    setSearchText("");
    setShowHighlighter(true);
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    // Ensure highlighter is shown when typing
    if (value && !showHighlighter) {
      setShowHighlighter(true);
    }
  };

  const tabsRef = React.useRef<HTMLInputElement>(null);

  // useUrlQuerySync(
  //   {
  //     searchText,
  //     section: selectedTab
  //   },
  //   (paramsFromUrl) => {
  //     if (paramsFromUrl.section && paramsFromUrl.section !== selectedTab) {
  //       const requestedSection = String(paramsFromUrl.section);
  //       // If privacy is enabled and user tries to access people tab, redirect to rooms instead
  //       const validSection =
  //         isOrgPrivacyEnabled && requestedSection === "people"
  //           ? "rooms"
  //           : requestedSection;
  //       setSelectedTab(validSection as "people" | "rooms" | "desks" | "other");
  //     }

  //     if (paramsFromUrl.searchText && paramsFromUrl.searchText !== searchText) {
  //       searchDisclosure.open();
  //       setSearchText(String(paramsFromUrl.searchText));
  //     }
  //   },
  //   router.isReady && (floors?.length || 0) > 0 && !isModal,
  //   false,
  //   true
  // );

  const onResourceClick = (resource: IResource | null) => {
    events.trigger("onFocusResource", resource);
    mapFiltersDisclosure.close();
  };

  const setResourceIdHovering = (resource: IResource | null) => {
    events.trigger("onHighlightResource", resource);
  };

  const setUserIdHovering = (userId: string | null) => {
    const resources = schedulable?.filter((resource) => {
      return (
        resource.schedules?.some((schedule) =>
          schedule.slots?.some((slot) => slot.user?.id === userId)
        ) || false
      );
    });
    events.trigger("onHighlightUserResource", resources || null);
  };

  const amenityItems = React.useMemo<SelectItem[]>(() => {
    return amenities.map((amenity) => ({
      id: amenity.id,
      name: amenity.name
    }));
  }, [amenities]);

  const selectedAmenityItems = React.useMemo<SelectItem[]>(() => {
    return amenitiesFilter.map((tag) => ({
      id: tag.id,
      name: tag.name
    }));
  }, [amenitiesFilter]);

  const filtersSelect = (
    <UnifiedSelect
      items={amenityItems}
      values={selectedAmenityItems}
      onSelectMultiple={(items) => {
        applyAmenitiesFilter(items.map((item) => item.id));
      }}
      multiselect
      withApply
      searchable
      searchPlaceholder="Search amenities..."
      placeholder="Filter amenities"
      trigger={
        isMd ? (
          <AmenitiesFilterTrigger count={amenitiesFilter.length} />
        ) : (
          <AmenitiesFilterButtonTrigger count={amenitiesFilter.length} />
        )
      }
      contentWidth="240px"
    />
  );

  // One entry per tab, keyed by its value so the configured order can be
  // applied without moving the tab bodies around.
  const tabsByValue: Record<
    MapTab,
    React.ComponentProps<typeof AnimatedTabs>["tabs"][number]
  > = {
    desks: getResourcesVirtualizedListTab({
      value: "desks",
      label: t("terms.desks"),
      resources: resources.desks,
      setResourceIdHovering,
      onReserve: onResourceClick,
      searchText,
      floorName: floor?.name || ""
    }),
    rooms: getResourcesVirtualizedListTab({
      value: "rooms",
      label: t("terms.rooms"),
      resources: resources.rooms,
      setResourceIdHovering,
      onReserve: onResourceClick,
      searchText,
      floorName: floor?.name || ""
    }),
    people: {
      value: "people",
      label: t("terms.people"),
      content: !users.length ? (
        searchText.length > 0 ? (
          <MapFiltersCardSearchNoResults
            target={"people"}
            searchText={searchText}
            floorName={floor?.name || ""}
          />
        ) : (
          <div className="text-center text-neutral-500 text-md">
            {getPlaceholderText("people")}
          </div>
        )
      ) : (
        <VirtualizedList
          itemHeight={86}
          scrollAreaProps={{
            className: classes.TabContentScrollArea
          }}
          isLoading={isFetching}
        >
          {users.map(({ user, schedules, dedicatedResource }) =>
            dedicatedResource ? (
              <UserLabel
                className={classes.ListItem}
                user={user}
                key={user.id}
                onMouseEnter={() => {
                  setResourceIdHovering(dedicatedResource);
                }}
                onClick={() => {
                  onResourceClick(dedicatedResource);
                }}
                onMouseLeave={() => setResourceIdHovering(null)}
                statusText={"Dedicated desk"}
                allowFavorite
                allowTessa
              />
            ) : (
              <UserLabel
                className={classes.ListItem}
                user={user}
                key={user.id}
                onMouseEnter={() => {
                  setUserIdHovering(user.id);
                }}
                onClick={() => {
                  userFocusDisclosure?.open(user);
                }}
                onMouseLeave={() => setUserIdHovering(null)}
                schedules={schedules}
                allowFavorite
                allowTessa
              />
            )
          )}
        </VirtualizedList>
      ),
      className: classes.TabsContent
    },
    other: getResourcesVirtualizedListTab({
      value: "other",
      label: t("terms.other"),
      resources: resources.other,
      setResourceIdHovering,
      onReserve: onResourceClick,
      searchText,
      floorName: floor?.name || ""
    })
  };

  const floorTabs = (
    <>
      {floor ? (
        <AnimatedTabs
          contentRef={tabsRef}
          onValueChange={handleTabChange}
          value={activeTab}
          className={"flex h-full flex-1 flex-col"}
          extraAction={
            <div
              className="flex flex-1 items-center justify-end"
              onClick={(e) => e.stopPropagation()}
            >
              {isMd && amenities.length > 0 && activeTab !== "people"
                ? filtersSelect
                : null}
            </div>
          }
          tabs={[
            // ...(isSearchInitialized.state
            //   ? [
            //       {
            //         value: "all",
            //         label: "All",
            //         content: (
            //           <MapSearchResults
            //             users={users?.map((user) => user.user) || []}
            //             resources={resources}
            //             setResourceIdHovering={setResourceIdHovering}
            //             onBook={onResourceClick}
            //             searchText={searchText}
            //           />
            //         )
            //       }
            //     ]
            //   : []),

            // Rendered in the organization's configured order; `tabsByValue`
            // is keyed so a reordered setting cannot change what a tab shows.
            ...visibleTabs.map((tab) => tabsByValue[tab]),
            // Search icon tab - only visible when search is not active
            ...(isMd && !searchDisclosure.isOpen
              ? [
                  {
                    value: "search-tab",
                    className: "py-0",
                    label: <SearchIcon className={"size-4 text-neutral-700"} />,
                    content: <></>
                  }
                ]
              : [])
          ]}
          defaultValue={firstTab}
        />
      ) : (
        <div className={"flex h-full w-full items-center justify-center"}>
          Select a Floor
        </div>
      )}
      {/* {tabsRef && searchDisclosure.isOpen && searchText.length > 0 && showHighlighter && (
        <TextHighlighter
          key={`${selectedTab}-${searchText}`}
          containerRef={tabsRef}
          searchTerm={searchText}
        />
      )} */}
    </>
  );

  const [isInternalModalOpen, setIsInternalModalOpen] = useState(false);
  const onInternalModalOpenChange = (showing: boolean) => {
    setIsInternalModalOpen(showing);
  };
  return (
    <>
      <div
        className={cn(
          "top-0",
          `fixed right-0 left-0 border-b-neutral-200 px-4 pb-2 md:hidden md:border-b md:bg-white [.v2-embedded-map_&]:flex [.v2-embedded-map_&]:px-4 [.v2-embedded-map_&]:py-2 ${MobileTopMargin}`,
          isInternalModalOpen ? "z-[100]" : "z-sticky"
        )}
      >
        <div className={"flex w-full items-center gap-2"}>
          <div
            className={
              "flex min-w-0 flex-1 max-w-[260px] overflow-hidden md:w-auto"
            }
          >
            <SelectOffices
              offices={currentOffice?.id ? [currentOffice.id] : []}
              onChange={(ids) => {
                const officeId = ids[0];
                if (officeId) {
                  changeOffice(officeId);
                }
              }}
              onSelectDivisionMap={(division) => {
                setViewingDivision(division);
              }}
              selectedDivisionMapId={viewingDivision?.id ?? null}
              triggerClassName="rounded-v2 max-w-full"
              trigger={
                <button
                  className={
                    "group inline-flex w-full min-w-0 max-w-full items-center justify-start gap-2 rounded-v2 h-[42px] md:bg-white md:border md:border-input px-3 md:hover:bg-accent/50 rounded-v2"
                  }
                >
                  <span
                    className={
                      "min-w-0 flex-1 truncate text-left font-bold text-[18px]"
                    }
                  >
                    {viewingDivision?.name || currentOffice.name}
                  </span>
                  {viewingDivision ? (
                    <span
                      className={
                        "flex-shrink-0 rounded bg-blue-50 px-1.5 py-0.5 font-semibold text-[10px] text-blue-700 uppercase tracking-wide"
                      }
                    >
                      Campus
                    </span>
                  ) : null}
                  <ArrowDownIcon
                    className={"size-4 flex-shrink-0 transition-opacity"}
                  />
                </button>
              }
            />
          </div>
          <div className={"ml-auto flex items-center gap-2"}>
            {!viewingDivision && (
              <div>
                <FloorSelector
                  floors={floors || []}
                  value={floor?.id || "0"}
                  onChangeFloor={onFloorChange}
                  allowEmpty
                />
              </div>
            )}
            {!viewingDivision && activeTab !== "people" && (
              <div>{filtersSelect}</div>
            )}
            <div className={"fixed bottom-16 left-3"}>
              <Button
                size={"sm"}
                className={
                  "border-1 border-neutral-200 bg-white text-neutral-1000 shadow-lg"
                }
                onClick={mapFiltersDisclosure.toggle}
              >
                <MenuIcon className={"size-4"} /> List View
              </Button>
            </div>
          </div>
        </div>
        <div className={"relative flex items-center gap-2 max-md:mt-1"}>
          {isCompact && (
            <EmbeddedScheduleButton
              onOverlayChange={onInternalModalOpenChange}
              currentOffice={currentOffice}
            />
          )}
          <CalendarInput
            mode={"single"}
            variant="outlined"
            onSelect={setSelectedDate}
            selectedDate={startDate || getNowDate()}
            //allowMultipleSwitch
            setValueOnApply
            withArrows={true}
          />
          <StartEndTimePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={changeStartDate}
            onEndDateChange={changeEndTime}
            onRangeChange={changeTimeRange}
            mode={timeMode}
            onModeChange={setTimeMode}
            className={
              "[.v2-embedded-map_&]:w-[230px] [.v2-embedded-map_&]:min-w-[230px] text-md"
            }
            allowLive
            timeTriggerClassName="text-sm"
            timeZone={currentOffice?.time_zone}
            officeStartHour={
              getDefaultsHoursFromOffice(currentOffice).startHour
            }
            officeEndHour={getDefaultsHoursFromOffice(currentOffice).endHour}
            defaultMeetingDuration={Math.round(
              getDefaultsHoursFromOffice(currentOffice).meetingRoomDuration * 60
            )}
            scrollable={true}
            scrollableHeightMultiplier={1.8}
          />
        </div>
      </div>
      {isMd && !isCompact && !fullscreenMapDisclosure.isOpen ? (
        <div
          // Remounts on mode change so the enter animation replays: the
          // panel slides back in when leaving campus mode, and the compact
          // campus card drops in when entering it.
          key={viewingDivision ? "campus-panel" : "office-panel"}
          className={cn(
            "hidden md:block",
            !viewingDivision && "h-full",
            viewingDivision
              ? "pointer-events-auto animate-in fade-in slide-in-from-top-2 duration-300"
              : "animate-in fade-in slide-in-from-left-6 duration-300"
          )}
        >
          <Card
            isFlat
            hasShadow={false}
            className={cn(
              `${SurfaceCardClasses} relative flex flex-col`,
              !viewingDivision && "h-full"
            )}
            titleWrapperClassName={"gap-3"}
            contentProps={{
              className: "flex justify-between flex-col flex-1"
            }}
            title={
              isMd && !isMapEditing ? (
                <SelectOffices
                  offices={currentOffice?.id ? [currentOffice.id] : []}
                  onChange={(ids) => {
                    const officeId = ids[0];
                    if (officeId) {
                      changeOffice(officeId);
                    }
                  }}
                  onSelectDivisionMap={(division) => {
                    setViewingDivision(division);
                  }}
                  selectedDivisionMapId={viewingDivision?.id ?? null}
                  trigger={
                    <button
                      className={
                        "group inline-flex w-full max-w-full items-center justify-start gap-1"
                      }
                    >
                      <span className={"min-w-0 truncate text-left"}>
                        {viewingDivision?.name || currentOffice.name}{" "}
                      </span>
                      {viewingDivision ? (
                        <span
                          className={
                            "flex-shrink-0 rounded bg-blue-50 px-1.5 py-0.5 font-semibold text-[10px] text-blue-700 uppercase tracking-wide"
                          }
                        >
                          Campus
                        </span>
                      ) : null}
                      <ArrowDownIcon
                        className={"size-4 flex-shrink-0 transition-opacity"}
                      />
                    </button>
                  }
                />
              ) : undefined
            }
            action={
              isMd ? (
                !isMapEditing &&
                dayViewDisclosure &&
                !isReserveOpen &&
                !viewingDivision ? (
                  <Switch
                    onCheckedChange={dayViewDisclosure.onOpenChange}
                    checked={dayViewDisclosure.isOpen}
                    variant={"squared"}
                    size={"xs"}
                  />
                ) : undefined
              ) : undefined
            }
          >
            {isMapEditing ? (
              <div
                className={
                  "pointer-events-none select-none py-2 text-center text-md text-neutral-800"
                }
              >
                This view is disabled when editing the map. Please exit edit
                mode to use it.
              </div>
            ) : viewingDivision ? (
              <div className={"flex flex-col gap-2 py-2"}>
                <p className={"text-neutral-700 text-sm"}>
                  Viewing the <strong>{viewingDivision.name}</strong> campus
                  map.
                </p>
                <p className={"text-neutral-400 text-xs"}>
                  Click a building on the map (or use the map legend) to open
                  its office. Selecting an office above exits the campus view.
                </p>
              </div>
            ) : (
              <>
                <div className={"hidden md:block"}>
                  <CalendarInput
                    mode={"single"}
                    variant="outlined"
                    onSelect={setSelectedDate}
                    selectedDate={startDate || getNowDate()}
                    //allowMultipleSwitch
                    setValueOnApply
                    withArrows={true}
                  />
                  <div className={classes.FloorAndTimeWrapper}>
                    <FloorSelector
                      floors={floors || []}
                      value={floor?.id}
                      onChangeFloor={onFloorChange}
                      allowEmpty
                    />
                    <div className={"w-full"}>
                      <StartEndTimePicker
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={changeStartDate}
                        onEndDateChange={changeEndTime}
                        onRangeChange={changeTimeRange}
                        mode={timeMode}
                        onModeChange={setTimeMode}
                        allowLive
                        timeTriggerClassName={
                          isModal
                            ? "text-sm !font-inter"
                            : "text-[13px] !font-inter"
                        }
                        timeZone={currentOffice?.time_zone}
                        officeStartHour={
                          getDefaultsHoursFromOffice(currentOffice).startHour
                        }
                        officeEndHour={
                          getDefaultsHoursFromOffice(currentOffice).endHour
                        }
                        defaultMeetingDuration={Math.round(
                          getDefaultsHoursFromOffice(currentOffice)
                            .meetingRoomDuration * 60
                        )}
                        scrollable={true}
                        scrollableHeightMultiplier={2}
                      />
                    </div>
                  </div>
                  <div
                    data-state={searchDisclosure.isOpen ? "open" : "closed"}
                    className={classes.SearchWrapper}
                  >
                    <TextInput
                      ref={searchRef}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          handleSearchOpenChange(false);
                        }
                      }}
                      placeholder={"Search..."}
                      leftIcon={<SearchIcon className={classes.SearchIcon} />}
                      rightSlot={[
                        <button key={"clear-icon"}>
                          <CloseIcon
                            className={classes.SearchCloseIcon}
                            onClick={handleSearchOpenChange.bind(null, false)}
                          />
                        </button>
                      ]}
                      onChange={(event) =>
                        handleSearchTextChange(event.target.value)
                      }
                      className="rounded-v2-md"
                      value={searchText}
                    />
                  </div>
                </div>

                {floorTabs}
              </>
            )}
          </Card>
        </div>
      ) : undefined}
      {!isMd || isCompact ? (
        <MobileDrawer
          isOpen={mapFiltersDisclosure.isOpen}
          onOpenChange={mapFiltersDisclosure.onOpenChange}
          revealGap={200}
          size={"full"}
          showBackdrop={false}
          scrollableContent={false}
          containerClassName={"static"}
          contentUnderGrabber
          dragOnTileOnly
        >
          <div className={"absolute top-10 right-3 bottom-0 left-4"}>
            {floorTabs}
          </div>
        </MobileDrawer>
      ) : undefined}
    </>
  );
};
